import React from 'react';
import { BarChart3 } from 'lucide-react';
import { C } from '../../theme.js';
import { Alert, Eyebrow, FieldLabel, TextInput } from '../../components/Primitives.jsx';
import { MultiColsDropdown } from './MultiColsDropdown.jsx';
import { AuditTable } from './AuditTable.jsx';
import { parseNumeric, resolveLiveRows, mergedColumns } from './columnClassification.js';
import { ColumnsContext, OriginalColumnsContext } from '../StepInspector.jsx';
import { hasUpstreamSplit, SOURCE_ROWS } from '../../data.js';

function getFitRows(rows) {
  if (!rows || !rows.length) return [];
  for (const key of Object.keys(rows[0] || {})) {
    const vals = new Set(rows.map(r => r[key]).filter(v => v !== null && v !== ''));
    if (vals.size > 0 && Array.from(vals).every(v => v === 'train' || v === 'test')) {
      const filtered = rows.filter(r => r[key] === 'train');
      if (filtered.length > 0) return filtered;
      break;
    }
  }
  return rows;
}

function computeVariancePreview(rows, selCols, threshold, manualOverrides = {}, allColsList = []) {
  if (!rows || !rows.length) return [];

  let evalCols = selCols;
  if (!evalCols || !evalCols.length) {
    evalCols = allColsList
      .filter(c => c.type === 'number' && c.name !== '__rowIndex')
      .map(c => c.name);
  }
  if (!evalCols.length && rows[0]) {
    evalCols = Object.keys(rows[0]).filter(k => k !== '__rowIndex' && parseNumeric(rows[0][k]) !== null);
  }
  if (!evalCols.length) return [];

  const fitRows = getFitRows(rows);
  const typeOf = (name) => (allColsList.find(c => c.name === name)?.type) || 'string';

  return evalCols.map(col => {
    const parsed = fitRows.map(r => parseNumeric(r[col]));
    const nums = parsed.filter(v => v !== null);
    const unparseable = parsed.length > 0 && nums.length === 0;

    let variance = 0.0;
    let isConst = false;

    if (nums.length < 2) {
      variance = 0.0;
      isConst = true;
    } else {
      const mean = nums.reduce((a, b) => a + b, 0) / nums.length;
      const ss = nums.reduce((sum, x) => sum + (x - mean) ** 2, 0);
      variance = ss / (nums.length - 1);
    }

    const roundVar = Math.round(variance * 10000) / 10000;
    // Keep only if variance is strictly greater than the threshold — see
    // the matching comment in the backend's s_variance_threshold for why
    // `>=` would silently defeat a threshold of exactly 0.0.
    const autoStatus = (variance > threshold && !isConst) ? 'keep' : 'drop';
    const override = manualOverrides[col] || 'auto';

    return {
      column: col,
      dataType: typeOf(col),
      variance: roundVar,
      isConst,
      unparseable,
      metric: unparseable ? 'N/A (unparseable)' : (isConst ? `${roundVar.toFixed(4)} (constant)` : roundVar.toFixed(4)),
      autoStatus,
      override
    };
  });
}

export function VarianceThresholdInspector({ step, onChange, pipeline, uploadedRows, onToast }) {
  const c = step.config || {};
  const sel = Array.isArray(c.columns) ? c.columns : (c.column ? [c.column] : []);
  const threshold = c.threshold ?? 0.0;
  const manualOverrides = c.manual_overrides || {};

  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const splitReady = pipeline ? hasUpstreamSplit(pipeline.steps, step.id) : true;
  const rows = resolveLiveRows(uploadedRows, pipeline, SOURCE_ROWS);

  const liveColumns = React.useContext(ColumnsContext) || [];
  const origColumns = React.useContext(OriginalColumnsContext) || [];
  const allCols = mergedColumns(origColumns, liveColumns);

  const preview = computeVariancePreview(rows, sel, threshold, manualOverrides, allCols);
  const unparseableCount = preview.filter(p => p.unparseable).length;

  const setOverride = (col, next) => {
    const nextOverrides = { ...manualOverrides };
    if (next === 'auto') {
      delete nextOverrides[col];
    } else {
      nextOverrides[col] = next;
    }
    u({ manual_overrides: nextOverrides });
    if (next === 'drop') {
      onToast?.({ kind: 'ok', title: 'Column dropped', detail: `'${col}' has been dropped.` });
    }
  };

  return (
    <div>
      <Eyebrow>Variance Threshold</Eyebrow>
      <div style={{ color: C.fg4, fontSize: '.78rem', margin: '4px 0 10px', lineHeight: 1.5 }}>
        Drop numerical features whose variance falls below the specified threshold.
      </div>

      {!splitReady && (
        <Alert kind="warn" style={{ marginBottom: 10 }}>
          No Train / Test Split step found upstream — variance is calculated using all rows. Add a Train / Test Split step first to avoid data leakage.
        </Alert>
      )}

      <div>
        <FieldLabel hint="Leave empty to check all numeric columns in the dataset">Select Columns (Optional)</FieldLabel>
        <MultiColsDropdown selected={sel} onChange={(v) => u({ columns: v, column: v[0] || null })} numeric />
      </div>

      <div style={{ marginTop: 12 }}>
        <FieldLabel hint="Columns with variance at or below this value are recommended for drop">Variance Threshold: {threshold}</FieldLabel>
        <TextInput
          type="number"
          step="0.001"
          min="0"
          value={threshold}
          onChange={(v) => u({ threshold: parseFloat(v) || 0.0 })}
          style={{ width: 120 }}
        />
      </div>

      {unparseableCount > 0 && (
        <Alert kind="warn" style={{ marginTop: 10 }}>
          {unparseableCount} column{unparseableCount === 1 ? '' : 's'} had no parseable numeric
          values in the training data (check for currency symbols, commas, or stray text) —
          treated as zero variance rather than causing an error.
        </Alert>
      )}

      <AuditTable
        headerLabel={<><BarChart3 size={12} style={{ display: 'inline', verticalAlign: -1, marginRight: 5 }} />Variance Threshold — candidate columns</>}
        rows={preview.map(row => ({
          column: row.column,
          dataType: row.dataType,
          metric: row.metric,
          autoStatus: row.autoStatus,
          override: row.override,
          onOverrideChange: (next) => setOverride(row.column, next),
        }))}
        emptyMessage="No numeric candidate columns found in the dataset schema."
      />
    </div>
  );
}

